import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Loader2, Search, X, Upload, ChevronLeft, ChevronRight, Radio, Activity, Flag } from 'lucide-react';
import { formatDT, getFileTimestamp } from '../../utils/timezone';
import { downloadCsv } from '../../utils/csv';
import { reverseGeocodeBatch, GOOGLE_MAPS_API_KEY } from '../../utils/geocode';
import { LocationPopover } from '../shared/LocationPopover';
import { NOMINATIM_BASE } from '../../utils/nominatim';

const WINDOW_OPTIONS = [1, 7, 30, 90];
const LIMIT = 25;

const beaconTypeLabel = (t) => (t === 'ibeacon' ? 'iBeacon' : t === 'eddystone' ? 'Eddystone' : 'Vendor / MAC');
const confidenceTone = (c) =>
  c === 'high' ? 'bg-green-50 text-green-700 border-green-200'
    : c === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-slate-100 text-slate-600 border-slate-200';

/**
 * Modal listing the REGISTERED beacons a given GPS tracker has detected, with a configurable
 * time window, search + type/purpose filters, per-beacon first/last seen, scan count, RSSI,
 * battery, confidence, last location, a CSV export (with resolved addresses) and a deep-link
 * to the existing Per-Beacon AVL Diagnostics. Data: GET /parsing/devices/{imei}/beacons.
 */
export const DeviceBeaconsModal = ({ open, onOpenChange, api, imei, deviceName, isFeatureEnabled, onOpenDiagnostics }) => {
  const [windowDays, setWindowDays] = useState(7);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [purpose, setPurpose] = useState('all');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalDetected, setTotalDetected] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!imei || !open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        window_days: String(windowDays), search, beacon_type: type, purpose,
        page: String(page), limit: String(LIMIT),
      });
      const res = await api.get(`/parsing/devices/${encodeURIComponent(imei)}/beacons?${params}`);
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
      setTotalDetected(res.data?.total_detected || 0);
    } catch (e) {
      console.error('Failed to load device beacons:', e);
      setRows([]); setTotal(0); setTotalDetected(0);
      toast.error('Failed to load detected beacons');
    } finally {
      setLoading(false);
    }
  }, [api, imei, open, windowDays, search, type, purpose, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput.trim()); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset filters when the modal opens for a (possibly new) device, and clear on close.
  useEffect(() => {
    if (open) {
      setPage(1);
    } else {
      setSearchInput(''); setSearch(''); setType('all'); setPurpose('all');
      setWindowDays(7); setRows([]); setTotal(0); setTotalDetected(0);
    }
  }, [open, imei]);

  const exportCsv = async () => {
    if (!rows.length) { toast.info('No beacons to export'); return; }
    setExporting(true);
    const tId = toast.loading('Resolving addresses…');
    try {
      const wantGoogle = !!isFeatureEnabled?.('google_reverse_geocoding') && !!GOOGLE_MAPS_API_KEY;
      const points = rows.map((b) => (b.last_location
        ? { lat: b.last_location.latitude, lng: b.last_location.longitude } : null));
      const addresses = await reverseGeocodeBatch(points, { useGoogle: wantGoogle, nominatimBaseUrl: NOMINATIM_BASE });
      downloadCsv(
        `device_${imei}_beacons_${getFileTimestamp()}.csv`,
        ['Beacon', 'Type', 'Purpose', 'First Seen', 'Last Seen', 'Scans', 'RSSI', 'Battery %', 'Confidence', 'Coordinates', 'Address'],
        rows.map((b, i) => [
          b.name, beaconTypeLabel(b.beacon_type), b.beacon_purpose || '',
          b.first_seen ? formatDT(b.first_seen) : '', b.last_seen ? formatDT(b.last_seen) : '',
          b.scan_count ?? 0, b.last_rssi ?? '', b.last_battery_percent ?? '', b.confidence || '',
          b.last_location ? `${b.last_location.latitude}, ${b.last_location.longitude}` : '', addresses[i] || '',
        ]),
      );
      toast.success(`Exported ${rows.length} beacon(s)`, { id: tId });
    } catch (e) {
      console.error('Device beacons export failed:', e);
      toast.error('Export failed', { id: tId });
    } finally {
      setExporting(false);
    }
  };

  const start = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const end = Math.min(page * LIMIT, total);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] flex flex-col" data-testid="device-beacons-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-[#2563eb]" /> Beacons Detected by {deviceName || imei}
          </DialogTitle>
          <DialogDescription data-testid="device-beacons-summary">
            <span className="font-medium text-foreground">{total}</span> registered {total === 1 ? 'beacon' : 'beacons'} of{' '}
            <span className="font-medium text-foreground">{totalDetected}</span> detected in the last {windowDays} {windowDays === 1 ? 'day' : 'days'}.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-8 pr-8 h-9 text-xs"
              placeholder="Search beacon name, IMEI or MAC…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              data-testid="device-beacons-search"
            />
            {searchInput && (
              <button type="button" onClick={() => setSearchInput('')} className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground" aria-label="Clear search" data-testid="device-beacons-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={String(windowDays)} onValueChange={(v) => { setWindowDays(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="device-beacons-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WINDOW_OPTIONS.map((d) => <SelectItem key={d} value={String(d)}>Last {d} {d === 1 ? 'day' : 'days'}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
            <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="device-beacons-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="ibeacon">iBeacon</SelectItem>
              <SelectItem value="eddystone">Eddystone</SelectItem>
              <SelectItem value="vendor_specific">Vendor / MAC</SelectItem>
            </SelectContent>
          </Select>
          <Select value={purpose} onValueChange={(v) => { setPurpose(v); setPage(1); }}>
            <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="device-beacons-purpose"><SelectValue placeholder="Purpose" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All purposes</SelectItem>
              <SelectItem value="asset">Asset</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="text-xs gap-1.5 h-9 px-3" onClick={exportCsv} disabled={exporting || !rows.length} data-testid="device-beacons-export">
            {exporting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving…</> : <><Upload className="w-3.5 h-3.5" /> Export CSV</>}
          </Button>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-y-auto rounded-md border border-border">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground" data-testid="device-beacons-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading beacons…
            </div>
          ) : rows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="px-3">Beacon</TableHead>
                  <TableHead className="px-3">Type</TableHead>
                  <TableHead className="px-3">Purpose</TableHead>
                  <TableHead className="px-3">First Seen</TableHead>
                  <TableHead className="px-3">Last Seen</TableHead>
                  <TableHead className="px-3 text-right">Scans</TableHead>
                  <TableHead className="px-3">RSSI</TableHead>
                  <TableHead className="px-3">Battery</TableHead>
                  <TableHead className="px-3">Confidence</TableHead>
                  <TableHead className="px-3">Last Location</TableHead>
                  <TableHead className="px-3 text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((b, i) => (
                  <TableRow key={b.id} className="text-xs" data-testid={`device-beacon-row-${i}`}>
                    <TableCell className="px-3 font-medium">{b.name}</TableCell>
                    <TableCell className="px-3">{beaconTypeLabel(b.beacon_type)}</TableCell>
                    <TableCell className="px-3 capitalize">{b.beacon_purpose || '-'}</TableCell>
                    <TableCell className="px-3" data-testid={`device-beacon-first-seen-${i}`}>
                      {b.first_seen
                        ? <span className="inline-flex items-center gap-1 font-medium text-[#2563eb]" title="First time this device detected the beacon"><Flag className="w-3 h-3" /> {formatDT(b.first_seen)}</span>
                        : <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell className="px-3">{b.last_seen ? formatDT(b.last_seen) : <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell className="px-3 text-right">{b.scan_count ?? 0}</TableCell>
                    <TableCell className="px-3">{b.last_rssi != null ? `${b.last_rssi} dBm` : '-'}</TableCell>
                    <TableCell className="px-3">
                      {b.last_battery_percent != null
                        ? <Badge variant="outline" title={b.last_battery_mv != null ? `${b.last_battery_mv} mV${b.battery_estimated ? ' (estimated from voltage)' : ''}` : undefined} className={(b.low_battery || b.last_battery_percent <= 20) ? 'bg-red-100 text-red-700' : 'bg-green-50 text-green-700'}>{b.battery_estimated ? '~' : ''}{b.last_battery_percent}%</Badge>
                        : <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell className="px-3"><Badge variant="outline" className={`capitalize ${confidenceTone(b.confidence)}`}>{b.confidence || '-'}</Badge></TableCell>
                    <TableCell className="px-3"><LocationPopover loc={b.last_location} testid={`device-beacon-location-${i}`} /></TableCell>
                    <TableCell className="px-3 text-right">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-[#2563eb]" onClick={() => onOpenDiagnostics?.(b.id)} data-testid={`device-beacon-diagnostics-${i}`}>
                        <Activity className="w-3.5 h-3.5" /> Diagnostics
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="device-beacons-empty">
              No registered beacons detected by this device in the selected window.
            </div>
          )}
        </div>

        {/* Pager */}
        <div className="flex items-center justify-between gap-3 pt-1 text-xs text-muted-foreground">
          <span data-testid="device-beacons-count">{total === 0 ? '0 results' : `${start}–${end} of ${total}`}</span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="device-beacons-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={end >= total} onClick={() => setPage((p) => p + 1)} data-testid="device-beacons-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
